import { useTranslation } from 'react-i18next';

import { SitePerspectiveType } from '@the-real-insight/tri-model';

import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Perspective, ProcessStep, ProcessType, perspectiveTitle, stepTitle } from './processModel';
import PerspectiveForm from './PerspectiveForm';
import { perspectiveAttributes } from './perspectiveAttributes';
import { PerspectiveScope, PerspectiveValues } from './sitePerspectiveApi';
import { PhaseParcelResult, PhaseParcelState } from './phaseParcelsApi';
import { sampleContentFor, sampleSource } from './sampleContent';
import type { SlotActions } from './SlotEditors';

/**
 * Drill-down for one perspective. What is shown is always the combination of
 * process type, process step and perspective — the same perspective means
 * something different in an acquisition than in a new build — so the panel names
 * all three, together with the property in view.
 */

interface Properties {
    type: ProcessType;
    step: ProcessStep;
    perspective: Perspective | null;
    /** Address of the property in view, or empty while none is picked. */
    objectLabel?: string;
    onDismiss: () => void;
    /** Stored values, where the perspective is backed by a `SitePerspective`. */
    perspectiveValues?: PerspectiveValues;
    /** Listenelemente und Werte gebundener Instanzen (SlotEditors). */
    slotActions?: SlotActions;
    onSavePerspective?: (
        type: SitePerspectiveType,
        values: Record<string, any>,
        comments?: Record<string, string>,
    ) => Promise<void>;
    /** False while no property is selected — there is no address to write against. */
    canEdit?: boolean;
    /** True when the phase is not activated yet. */
    perspectivesLocked?: boolean;
    /** The phase on screen, for the points that read from it rather than from the perspective. */
    perspectiveScope?: PerspectiveScope | null;
    /** The building's outline, drawn under the parcels of the plot. */
    footprint?: any;
    /** Takes the whole plot back after a parcel decision — outlines, area and the figures it moved. */
    onParcelsChanged?: (result: PhaseParcelResult) => void;
    /** Phase parcel state from the page — map and table share one read. */
    parcelState?: PhaseParcelState;
}

export default function PerspectiveDrillDown({
    type,
    step,
    perspective,
    objectLabel,
    onDismiss,
    perspectiveValues,
    onSavePerspective,
    slotActions,
    canEdit,
    perspectivesLocked,
    perspectiveScope,
    footprint,
    onParcelsChanged,
    parcelState,
}: Properties) {
    const { t, i18n } = useTranslation();
    const content = perspective ? sampleContentFor(type.key, step.key, perspective.key) : undefined;
    const backing = perspective?.perspectiveType;

    return (
        <Sheet
            open={!!perspective}
            onOpenChange={(next) => {
                if (!next) onDismiss();
            }}
        >
            <SheetContent
                side="right"
                className="flex w-[min(96vw,1100px)] max-w-none flex-col gap-0 p-0 sm:max-w-none"
            >
                <SheetHeader className="space-y-0 border-b p-4 pr-12">
                    <SheetTitle>
                        {perspective ? perspectiveTitle(perspective, t, i18n.language ?? 'de') : ''}
                    </SheetTitle>
                </SheetHeader>

                <div className="min-w-0 flex-1 overflow-auto p-4">
                    {perspective ? (
                        <div>
                            <div className="flex flex-wrap items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                                <span>{t(type.labelKey)}</span>
                                <span aria-hidden>›</span>
                                <span>{stepTitle(step, t, i18n.language ?? 'de')}</span>
                                <span aria-hidden>›</span>
                                <span>{perspectiveTitle(perspective, t, i18n.language ?? 'de')}</span>
                            </div>
                            {objectLabel ? <div className="mt-1.5 text-base font-bold">{objectLabel}</div> : <></>}

                            {backing && perspectivesLocked ? (
                                <div className="mt-5 rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                                    {t('portfolioDashboard.activation.locked')}
                                </div>
                            ) : backing && onSavePerspective ? (
                                <div className="mt-6">
                                    <PerspectiveForm
                                        type={backing}
                                        attributes={perspectiveAttributes(type.key, step.key, backing)}
                                        values={perspectiveValues?.[backing]}
                                        onSave={onSavePerspective}
                                        slotActions={slotActions}
                                        disabled={!canEdit}
                                        scope={perspectiveScope}
                                        parcelState={parcelState}
                                        footprint={footprint}
                                        onParcelsChanged={onParcelsChanged}
                                    />
                                </div>
                            ) : content ? (
                                <>
                                    <Badge variant="warning" className="mt-3 uppercase tracking-wide">
                                        {t('portfolioDashboard.sample.badge')}
                                    </Badge>
                                    <div className="mt-3 text-lg font-bold leading-snug">{content.headline}</div>

                                    {content.sections.map((section) => (
                                        <div key={section.title} className="mt-6">
                                            <div className="mb-1.5 text-xs font-bold uppercase tracking-[0.08em] text-muted-foreground">
                                                {section.title}
                                            </div>
                                            <div className="flex flex-col">
                                                {section.metrics.map((metric) => (
                                                    <div
                                                        key={metric.label}
                                                        className="flex items-baseline justify-between gap-4 border-b py-1.5"
                                                    >
                                                        <span className="text-sm text-muted-foreground">
                                                            {metric.label}
                                                        </span>
                                                        <span className="text-right text-sm font-bold">
                                                            {metric.value}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    {content.notes && content.notes.length > 0 ? (
                                        <div className="mt-6">
                                            <div className="mb-1.5 text-xs font-bold uppercase tracking-[0.08em] text-muted-foreground">
                                                {t('portfolioDashboard.drillDown.findings')}
                                            </div>
                                            <ul className="m-0 list-disc pl-5 text-sm leading-relaxed [&>li+li]:mt-1.5">
                                                {content.notes.map((note) => (
                                                    <li key={note}>{note}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : (
                                        <></>
                                    )}

                                    <div className="mt-6 border-t pt-3 text-xs leading-relaxed text-muted-foreground">
                                        {t('portfolioDashboard.sample.source', {
                                            object: sampleSource.object,
                                            document: sampleSource.document,
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="mt-5 rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                                    {t('portfolioDashboard.drillDown.empty')}
                                </div>
                            )}
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
